import React, { useState } from 'react';
import { EyeIcon, EditIcon, Trash2Icon, InfoIcon } from 'lucide-react';
import { ToggleSwitch } from './ToggleSwitch';
import { Tooltip } from '../Tooltip';
interface CollectionRowProps {
  collection: {
    id: string;
    name: string;
    spuCount: number;
    skuCount: number;
    individualSkuCount: number;
    displaySequence: number;
    isPopular: boolean;
    type: string;
  };
  onTogglePopular: (id: string, status: boolean) => void;
  onSequenceChange: (id: string, sequence: string) => void;
  onDelete: (id: string) => void;
}
export const CollectionRow = ({
  collection,
  onTogglePopular,
  onSequenceChange,
  onDelete
}: CollectionRowProps) => {
  const [sequence, setSequence] = useState(collection.displaySequence.toString());
  const [isEditing, setIsEditing] = useState(false);
  const handleSequenceBlur = () => {
    setIsEditing(false);
    if (sequence !== collection.displaySequence.toString()) {
      onSequenceChange(collection.id, sequence);
    }
  };
  const handleSequenceKeyDown = e => {
    if (e.key === 'Enter') {
      handleSequenceBlur();
    }
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'spu':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-106">
            SPU Based
          </span>;
      case 'sku':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-107">
            SKU Specific
          </span>;
      case 'mixed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-108">
            Mixed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-109">
            Unknown
          </span>;
    }
  };
  const getItemsCountTooltip = () => {
    const totalSKUs = collection.skuCount + collection.individualSkuCount;
    if (collection.type === 'spu') {
      return `${collection.spuCount} SPUs (${collection.skuCount} total SKUs)`;
    } else if (collection.type === 'sku') {
      return `${collection.individualSkuCount} individual SKUs`;
    } else {
      return `${collection.spuCount} SPUs (${collection.skuCount} total SKUs), ${collection.individualSkuCount} individual SKUs`;
    }
  };
  return <tr className="hover:bg-gray-50 transition-colors" data-id="element-110">
      <td className="px-6 py-4" data-id="element-111">
        <div className="flex items-center" data-id="element-112">
          <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center mr-3" data-id="element-113">
            <span className="text-gray-500 text-xs font-medium" data-id="element-114">
              {collection.name.substring(0, 2).toUpperCase()}
            </span>
          </div>
          <span className="font-medium text-gray-900" data-id="element-115">{collection.name}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-116">
        <div className="flex items-center" data-id="element-117">
          <div className="text-sm text-gray-900" data-id="element-118">
            {collection.spuCount > 0 && <span className="mr-1" data-id="element-119">{collection.spuCount} SPUs,</span>}
            {collection.skuCount + collection.individualSkuCount} SKUs
          </div>
          <Tooltip content={getItemsCountTooltip()} data-id="element-120">
            <InfoIcon className="h-3.5 w-3.5 ml-1 text-gray-400" data-id="element-121" />
          </Tooltip>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-122">
        {isEditing ? <input type="number" min="1" value={sequence} onChange={e => setSequence(e.target.value)} onBlur={handleSequenceBlur} onKeyDown={handleSequenceKeyDown} className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" autoFocus data-id="element-123" /> : <div className="text-sm text-gray-900 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded w-16 text-center" onClick={() => setIsEditing(true)} data-id="element-124">
            {collection.displaySequence}
          </div>}
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-125">
        <ToggleSwitch checked={collection.isPopular} onChange={checked => onTogglePopular(collection.id, checked)} data-id="element-126" />
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-id="element-127">
        {getTypeLabel(collection.type)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-128">
        <div className="flex items-center justify-end space-x-3" data-id="element-129">
          <button className="text-blue-600 hover:text-blue-800" data-id="element-130">
            <EyeIcon className="h-5 w-5" data-id="element-131" />
          </button>
          <button className="text-gray-600 hover:text-gray-900" data-id="element-132">
            <EditIcon className="h-5 w-5" data-id="element-133" />
          </button>
          <button className="text-red-600 hover:text-red-800" onClick={() => onDelete(collection.id)} data-id="element-134">
            <Trash2Icon className="h-5 w-5" data-id="element-135" />
          </button>
        </div>
      </td>
    </tr>;
};